/**
 * The pathway builder's editable draft and its conversions, as pure functions
 * (contract §2, §5.1 `PathwayIn`, §9 builder).
 *
 *   AdminPathway (server) --toDraft--> Draft (form state) --toPathwayIn--> PathwayIn (POST/PUT body)
 *
 * WHY A SEPARATE DRAFT SHAPE rather than editing the AdminPathway in place:
 *   - Number fields are edited as STRINGS ("" is a legitimate value while typing, and ""
 *     also means "unlimited" for the capacity). The price is edited in MAJOR units
 *     ("49.00") and sent as integer `price_cents`. They become integers only in
 *     `toPathwayIn`.
 *   - Every level, step and quiz question carries a client-only `key` for React identity
 *     and for the editing operations. For a row the server knows, `key === id`. A new row
 *     gets `new-<n>` and `id: null`. The key is NEVER serialized: a new row travels
 *     without an `id`, and the server assigns one (contract §2, "new rows get server ids").
 *   - A step holds the content objects of ALL six kinds (`video`, `reading`, `practice`,
 *     `quiz`, `live`, `course`), so switching the kind of a NEW step in the step editor
 *     and back loses nothing. `toPathwayIn` sends only the object of the step's kind
 *     (STEP_CONTENT_MISMATCH otherwise).
 *   - A SAVED step keeps its kind, and a saved course step keeps its course (contract
 *     §10.1, STEP_IDENTITY_CHANGED). `isIdentityLocked` is the one rule the editor reads:
 *     a step with a server id is locked; a step still without one is not.
 *   - A live step carries both halves (`starts_at` for a cohort, `service_id` for a
 *     self-paced pathway), and `toPathwayIn` sends the half of the pathway's CURRENT
 *     schedule, the other null, so switching the schedule loses nothing either.
 *   - The read-only resolutions the server adds to a step (`live.service`,
 *     `course.info`, contract §10.6) travel in the draft for display (the canvas row,
 *     the editor's paid-course warning) and are never sent.
 *   - Media travel as `MediaRef`s in the draft (filename, size, url: what the editor
 *     shows) and as bare ids in `PathwayIn` (`video.media_id`, `reading.pdf_media_id`,
 *     `resource_media_ids`): the server resolves the rest (contract §2).
 *
 * STRUCTURAL RULES the model enforces, because the server refuses their violation even on
 * a draft (contract §2):
 *   - `successive` and `alternative` hold EXACTLY ONE level (PATH_TYPE_ONE_LEVEL). The
 *     draft of a flat pathway therefore always holds one level, and a switch from a
 *     multi-level `level` pathway to a flat type merges the levels, in order
 *     (`setPathType`, which the screen confirms first).
 *   - In `successive` every step is mandatory: `toPathwayIn` sends `required: true`
 *     whatever the draft says. The draft keeps the author's flags, so a round trip
 *     through `successive` and back to `alternative` does not erase them before a save.
 *   - The single level of a flat pathway is never shown, but its title is still required
 *     (1..120): it is sent as its own title, or as the pathway's title when blank.
 *
 * Validation here is the contract's SHAPE rules only (§2 bounds, cohort dates). Whether
 * the pathway can be ACTIVATED (an objective, a step, a mandatory step per level, content
 * on every step, cohort dates, contract §4) is the server's call, returned as
 * `activation_problems` on every AdminPathway and as the problems of a 422
 * PATHWAY_NOT_ACTIVATABLE. It is never recomputed here: a second judge would eventually
 * disagree with the first.
 */

import { videoEmbedUrl } from "../lib/videoUrls";

// ── bounds (contract §2) ─────────────────────────────────────────────────────
export const TITLE_MAX = 120;
export const DESCRIPTION_MAX = 5000;
export const OBJECTIVES_MAX = 6;
export const OBJECTIVE_MAX = 200;
export const CAPACITY_MAX = 100000;
export const PRICE_CENTS_MAX = 10000000;
/** Smallest paid price: Stripe refuses a charge below it (admin/money.js MIN_CENTS). */
export const PAID_PRICE_MIN_CENTS = 50;
export const LEVELS_MAX = 20;
export const LEVEL_TITLE_MAX = 120;
export const STEPS_PER_LEVEL_MAX = 50;
export const STEPS_TOTAL_MAX = 200;
export const STEP_TITLE_MAX = 160;
export const DURATION_MAX = 1440;
export const READING_BODY_MAX = 20000;
export const QUESTIONS_MAX = 50;
export const QUESTION_MAX = 1000;
export const CHOICES_MIN = 2;
export const CHOICES_MAX = 6;
export const CHOICE_MAX = 300;
export const RESOURCES_MAX = 10;
export const DEFAULT_PASS_THRESHOLD = 70;
export const PRACTICE_INSTRUCTIONS_MAX = 5000;
export const LOCATION_NOTE_MAX = 300;

export const PATH_TYPES = Object.freeze(["successive", "alternative", "level"]);
export const SCHEDULES = Object.freeze(["cohort", "self_paced"]);
/** The kind selector's order (contract §10.0.1): the spec's five, then the owner's `course`. */
export const STEP_KINDS = Object.freeze(["video", "reading", "practice", "quiz", "live", "course"]);

const INT = /^\d+$/;
const MONEY = /^\d+(?:[.,]\d{1,2})?$/;

/** "12" -> 12; anything that is not a plain non-negative integer -> NaN. */
const toInt = (v) => (INT.test(String(v ?? "").trim()) ? Number(String(v).trim()) : NaN);
const inRange = (v, lo, hi) => Number.isInteger(v) && v >= lo && v <= hi;
const trimmed = (v) => String(v ?? "").trim();

/**
 * A price typed in major units ("49", "49.5", "49,50", "") -> integer cents; NaN when it
 * is not an amount. "" is 0 (free).
 */
export function priceToCents(v) {
  const s = trimmed(v);
  if (s === "") return 0;
  if (!MONEY.test(s)) return NaN;
  return Math.round(Number(s.replace(",", ".")) * 100);
}

/** Integer cents -> "49.00" for the price input; "0" for a free pathway. */
export const centsToPrice = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n) || n <= 0) return "0";
  return (n / 100).toFixed(2);
};

/** Whether a pasted video link is one the server accepts (lib/videoUrls mirrors its rule). */
export const isAcceptedVideoLink = (url) => videoEmbedUrl(trimmed(url)) !== "";

// ── server -> draft ──────────────────────────────────────────────────────────

/** A MediaRef as the draft keeps it (the admin `url` is for previews only). */
const mediaToDraft = (m) => (m && typeof m.media_id === "string"
  ? {
    media_id: m.media_id,
    filename: m.filename || "",
    size: Number.isFinite(m.size) ? m.size : 0,
    content_type: m.content_type || "",
    url: m.url || "",
  }
  : null);

const emptyVideo = () => ({ source: "upload", media: null, url: "" });
const emptyReading = () => ({ body: "", pdf: null });
const emptyQuiz = () => ({ questions: [], pass_threshold: String(DEFAULT_PASS_THRESHOLD) });
const emptyPractice = () => ({ instructions: "", submission_required: false });
/** `starts_at` is the UTC instant or ""; `service` is the server's resolution (read-only). */
const emptyLive = () => ({ starts_at: "", location_note: "", service_id: "", service: null });
/** `info` is the server's resolution of the course (read-only). */
const emptyCourse = () => ({ course_id: "", info: null });

const practiceToDraft = (p) => (p
  ? { instructions: p.instructions || "", submission_required: Boolean(p.submission_required) }
  : emptyPractice());

const liveToDraft = (l) => (l
  ? {
    starts_at: l.starts_at || "",
    location_note: l.location_note || "",
    service_id: l.service_id || "",
    service: l.service || null,
  }
  : emptyLive());

const courseToDraft = (c) => (c && c.course_id ? { course_id: c.course_id, info: c.info || null } : emptyCourse());

const videoToDraft = (v) => {
  if (!v) return emptyVideo();
  if (v.source === "link") return { source: "link", media: null, url: v.url || "" };
  return { source: "upload", media: mediaToDraft(v.media || v), url: "" };
};

const questionToDraft = (q) => ({
  key: q.id,
  id: q.id,
  question: q.question || "",
  choices: Array.isArray(q.choices) ? [...q.choices] : ["", ""],
  correct_index: Number.isInteger(q.correct_index) ? q.correct_index : 0,
});

const stepToDraft = (s) => ({
  key: s.id,
  id: s.id,
  kind: STEP_KINDS.includes(s.kind) ? s.kind : "reading",
  title: s.title || "",
  duration_minutes: String(s.duration_minutes ?? 0),
  required: s.required !== false,
  video: s.kind === "video" ? videoToDraft(s.video) : emptyVideo(),
  reading: s.kind === "reading" && s.reading
    ? { body: s.reading.body || "", pdf: mediaToDraft(s.reading.pdf) }
    : emptyReading(),
  quiz: s.kind === "quiz" && s.quiz
    ? {
      questions: (s.quiz.questions || []).map(questionToDraft),
      pass_threshold: String(s.quiz.pass_threshold ?? DEFAULT_PASS_THRESHOLD),
    }
    : emptyQuiz(),
  practice: s.kind === "practice" ? practiceToDraft(s.practice) : emptyPractice(),
  live: s.kind === "live" ? liveToDraft(s.live) : emptyLive(),
  course: s.kind === "course" ? courseToDraft(s.course) : emptyCourse(),
  resources: (s.resources || []).map(mediaToDraft).filter(Boolean),
});

/**
 * @param {object} p AdminPathway
 * @returns {object} Draft
 */
export function toDraft(p) {
  const levels = (p.levels || []).map((l) => ({
    key: l.id,
    id: l.id,
    title: l.title || "",
    steps: (l.steps || []).map(stepToDraft),
  }));
  return {
    title: p.title || "",
    description: p.description || "",
    objectives: Array.isArray(p.objectives) && p.objectives.length > 0 ? [...p.objectives] : [""],
    certificate_enabled: Boolean(p.certificate_enabled),
    path_type: PATH_TYPES.includes(p.path_type) ? p.path_type : "successive",
    schedule: p.schedule === "cohort" ? "cohort" : "self_paced",
    start_date: p.start_date || "",
    end_date: p.end_date || "",
    capacity: p.capacity == null ? "" : String(p.capacity),
    price: centsToPrice(p.price_cents),
    status: p.status || "draft",
    // Flat types always hold their one level, even if a stored document came back empty.
    levels: levels.length > 0 ? levels : [newLevel("level-0")],
  };
}

// ── new rows ─────────────────────────────────────────────────────────────────

export const newLevel = (key) => ({ key, id: null, title: "", steps: [] });

/** The draft of a brand-new pathway (the builder at /admin/pathways/new). */
export const emptyDraft = () => ({
  title: "",
  description: "",
  objectives: [""],
  certificate_enabled: false,
  path_type: "successive",
  schedule: "self_paced",
  start_date: "",
  end_date: "",
  capacity: "",
  price: "0",
  status: "draft",
  levels: [newLevel("level-0")],
});

/**
 * A new step of `kind`, not yet in any level: the step editor edits it and "Save step"
 * inserts it.
 *
 * @param {string} key
 * @param {"video"|"reading"|"practice"|"quiz"|"live"|"course"} kind
 */
export const newStep = (key, kind = "video") => ({
  key,
  id: null,
  kind,
  title: "",
  duration_minutes: "0",
  required: true,
  video: emptyVideo(),
  reading: emptyReading(),
  quiz: emptyQuiz(),
  practice: emptyPractice(),
  live: emptyLive(),
  course: emptyCourse(),
  resources: [],
});

/**
 * Whether a step's kind (and, for a course step, its course) is fixed: the server
 * refuses to change either on a stored step (contract §10.1, 422 STEP_IDENTITY_CHANGED).
 * A step is stored once it has a server id; a step added since the last save has none.
 */
export const isIdentityLocked = (s) => Boolean(s && s.id);

/**
 * The questions of a course quiz (`CourseQuiz`, contract §10.5) as draft questions to
 * APPEND to a quiz step: a copy, with new client keys and no id (later edits to the
 * course do not propagate). The server already left out what a pathway quiz cannot hold.
 *
 * @param {{ questions: { question: string, choices: string[], correct_index: number }[] }} courseQuiz
 * @param {() => string} nextKey
 */
export const importedQuestions = (courseQuiz, nextKey) => (courseQuiz.questions || []).map((q) => ({
  key: nextKey(),
  id: null,
  question: q.question || "",
  choices: Array.isArray(q.choices) ? [...q.choices] : ["", ""],
  correct_index: Number.isInteger(q.correct_index) ? q.correct_index : 0,
}));

/** The minutes a course step is prefilled with: the sum of the course's lesson durations, capped. */
export const courseDurationMinutes = (course) => Math.min(
  DURATION_MAX,
  (course?.lessons || []).reduce((n, l) => n + (Number.isFinite(l.duration_minutes) ? Math.max(0, l.duration_minutes) : 0), 0),
);

/** @param {string} key */
export const newQuestion = (key) => ({ key, id: null, question: "", choices: ["", ""], correct_index: 0 });

// ── draft -> PathwayIn ───────────────────────────────────────────────────────

const withId = (row) => (row.id ? { id: row.id } : {});

const questionToIn = (q) => ({
  ...withId(q),
  question: trimmed(q.question),
  choices: (q.choices || []).map(trimmed),
  correct_index: Number.isInteger(q.correct_index) ? q.correct_index : 0,
});

/**
 * The content object of a step's kind. An upload video with no file and a link video with
 * no address send no `video` at all: an empty video is a draft's business, refused only
 * at activation (VIDEO_EMPTY, contract §4). A live step without its time or session type
 * is sent the same way, with the missing half null (LIVE_TIME_REQUIRED /
 * LIVE_SERVICE_REQUIRED at activation, contract §10.1).
 */
const contentToIn = (s, schedule) => {
  if (s.kind === "video") {
    const v = s.video;
    if (v.source === "link" && trimmed(v.url)) return { video: { source: "link", url: trimmed(v.url) } };
    if (v.source === "upload" && v.media) return { video: { source: "upload", media_id: v.media.media_id } };
    return {};
  }
  if (s.kind === "reading") {
    return { reading: { body: s.reading.body, pdf_media_id: s.reading.pdf ? s.reading.pdf.media_id : null } };
  }
  if (s.kind === "practice") {
    return { practice: { instructions: s.practice.instructions, submission_required: Boolean(s.practice.submission_required) } };
  }
  if (s.kind === "live") {
    const cohort = schedule === "cohort";
    return {
      live: {
        starts_at: cohort && s.live.starts_at ? s.live.starts_at : null,
        location_note: trimmed(s.live.location_note),
        service_id: !cohort && s.live.service_id ? s.live.service_id : null,
      },
    };
  }
  if (s.kind === "course") return { course: { course_id: s.course.course_id } };
  return {
    quiz: {
      questions: s.quiz.questions.map(questionToIn),
      pass_threshold: toInt(s.quiz.pass_threshold),
    },
  };
};

const stepToIn = (s, pathType, schedule) => ({
  ...withId(s),
  kind: s.kind,
  title: trimmed(s.title),
  duration_minutes: toInt(s.duration_minutes),
  required: pathType === "successive" ? true : Boolean(s.required),
  ...contentToIn(s, schedule),
  resource_media_ids: s.resources.map((r) => r.media_id),
});

/**
 * The FULL body of `POST /pathways` and `PUT /pathways/{id}`. Call it on a draft that
 * `validateDraft` accepted; otherwise number fields may serialize as NaN.
 *
 * `status` is not part of PathwayIn (the server preserves it, and a transition is its own
 * endpoint), so it is not sent.
 *
 * @param {object} d Draft
 * @returns {object} PathwayIn
 */
export function toPathwayIn(d) {
  const cohort = d.schedule === "cohort";
  const flat = d.path_type !== "level";
  return {
    title: trimmed(d.title),
    description: d.description,
    objectives: d.objectives.map(trimmed).filter(Boolean),
    certificate_enabled: Boolean(d.certificate_enabled),
    path_type: d.path_type,
    schedule: d.schedule,
    start_date: cohort && d.start_date ? d.start_date : null,
    end_date: cohort && d.end_date ? d.end_date : null,
    capacity: trimmed(d.capacity) === "" ? null : toInt(d.capacity),
    price_cents: priceToCents(d.price),
    levels: d.levels.map((l) => ({
      ...withId(l),
      title: flat ? (trimmed(l.title) || trimmed(d.title)) : trimmed(l.title),
      steps: l.steps.map((s) => stepToIn(s, d.path_type, d.schedule)),
    })),
  };
}

// ── validation (contract §2 shape rules) ─────────────────────────────────────

/**
 * The shape problems of one step, keyed by field, as "admin" i18n keys. Used by the step
 * editor before "Save step" and by `validateDraft` before a save.
 *
 *   "title" | "duration" | "video" | "body" | "questions" | "threshold" | "resources"
 *   "instructions" | "location" | "startsAt" | "course"
 *
 * @param {object} s a draft step
 * @returns {Object<string, string>} {} when the step can be kept
 */
export function validateStep(s) {
  const errors = {};
  const title = trimmed(s.title);
  if (!title || title.length > STEP_TITLE_MAX) errors.title = "path.error.stepTitle";
  if (!inRange(toInt(s.duration_minutes), 0, DURATION_MAX)) errors.duration = "path.error.duration";
  if (s.kind === "video" && s.video.source === "link" && trimmed(s.video.url) && !isAcceptedVideoLink(s.video.url)) {
    errors.video = "path.error.videoUrl";
  }
  if (s.kind === "reading" && s.reading.body.length > READING_BODY_MAX) errors.body = "path.error.readingBody";
  if (s.kind === "practice" && s.practice.instructions.length > PRACTICE_INSTRUCTIONS_MAX) {
    errors.instructions = "path.error.practiceInstructions";
  }
  if (s.kind === "live") {
    if (trimmed(s.live.location_note).length > LOCATION_NOTE_MAX) errors.location = "path.error.locationNote";
    if (s.live.starts_at && !Number.isFinite(Date.parse(s.live.starts_at))) errors.startsAt = "path.error.startsAt";
  }
  // A course step names its course from the start: unlike an empty video, there is no
  // activation problem for a missing course, and the server refuses an unknown one.
  if (s.kind === "course" && !s.course.course_id) errors.course = "path.error.courseRequired";
  if (s.kind === "quiz") {
    if (!inRange(toInt(s.quiz.pass_threshold), 0, 100)) errors.threshold = "path.error.threshold";
    const qs = s.quiz.questions;
    if (qs.length > QUESTIONS_MAX) errors.questions = "path.error.tooManyQuestions";
    else {
      const problem = qs.map(questionProblem).find(Boolean);
      if (problem) errors.questions = problem;
    }
  }
  if (s.resources.length > RESOURCES_MAX) errors.resources = "path.error.tooManyResources";
  return errors;
}

/** The first shape problem of one quiz question, as an "admin" i18n key, or null. */
function questionProblem(q) {
  const text = trimmed(q.question);
  if (!text || text.length > QUESTION_MAX) return "path.error.questionText";
  const choices = (q.choices || []).map(trimmed);
  if (choices.length < CHOICES_MIN || choices.length > CHOICES_MAX) return "path.error.choicesCount";
  if (choices.some((c) => !c || c.length > CHOICE_MAX)) return "path.error.choiceText";
  if (!inRange(q.correct_index, 0, choices.length - 1)) return "path.error.correctChoice";
  return null;
}

/**
 * Every shape problem of the draft, keyed by field. The values are "admin" i18n keys.
 *
 *   "title" | "description" | "objectives" | "capacity" | "price" | "dates" | "levels"
 *   "level:<key>:title" | "level:<key>:steps"
 *   "step:<key>"                              (a step whose editor would refuse it)
 *
 * @param {object} d Draft
 * @returns {Object<string, string>} {} when the draft can be sent
 */
export function validateDraft(d) {
  const errors = {};
  const title = trimmed(d.title);
  if (!title || title.length > TITLE_MAX) errors.title = "path.error.title";
  if (d.description.length > DESCRIPTION_MAX) errors.description = "path.error.description";
  const objectives = d.objectives.map(trimmed).filter(Boolean);
  if (objectives.length > OBJECTIVES_MAX) errors.objectives = "path.error.objectivesMax";
  else if (objectives.some((o) => o.length > OBJECTIVE_MAX)) errors.objectives = "path.error.objectiveLength";
  if (trimmed(d.capacity) !== "" && !inRange(toInt(d.capacity), 1, CAPACITY_MAX)) errors.capacity = "path.error.capacity";
  const cents = priceToCents(d.price);
  if (!Number.isInteger(cents) || cents < 0 || cents > PRICE_CENTS_MAX) errors.price = "path.error.price";
  else if (cents > 0 && cents < PAID_PRICE_MIN_CENTS) errors.price = "path.error.priceMinimum";
  if (d.schedule === "cohort" && d.start_date && d.end_date && d.end_date < d.start_date) {
    errors.dates = "path.error.cohortDates";
  }

  if (d.levels.length > LEVELS_MAX) errors.levels = "path.error.tooManyLevels";
  const total = d.levels.reduce((n, l) => n + l.steps.length, 0);
  if (total > STEPS_TOTAL_MAX) errors.levels = "path.error.tooManySteps";
  for (const l of d.levels) {
    if (d.path_type === "level") {
      const lt = trimmed(l.title);
      if (!lt || lt.length > LEVEL_TITLE_MAX) errors[`level:${l.key}:title`] = "path.error.levelTitle";
    }
    if (l.steps.length > STEPS_PER_LEVEL_MAX) errors[`level:${l.key}:steps`] = "path.error.tooManyLevelSteps";
    for (const s of l.steps) {
      if (Object.keys(validateStep(s)).length > 0) errors[`step:${s.key}`] = "path.error.stepInvalid";
    }
  }
  return errors;
}

// ── path type ────────────────────────────────────────────────────────────────

/**
 * Whether switching the draft to `pathType` merges levels (and so needs the author's
 * confirmation): a `level` pathway with more than one level going flat.
 */
export const switchMergesLevels = (d, pathType) => pathType !== "level" && d.levels.length > 1;

/**
 * The draft with its path type changed. Going flat MERGES every level into the first one,
 * steps in order (level 1's steps, then level 2's, ...), keeping the first level's id and
 * title, because a flat pathway holds exactly one level (contract §2).
 */
export function setPathType(d, pathType) {
  if (!PATH_TYPES.includes(pathType) || pathType === d.path_type) return d;
  if (pathType === "level" || d.levels.length <= 1) return { ...d, path_type: pathType };
  const [first] = d.levels;
  return {
    ...d,
    path_type: pathType,
    levels: [{ ...first, steps: d.levels.flatMap((l) => l.steps) }],
  };
}

// ── structural edits ─────────────────────────────────────────────────────────

/** Where a step sits: `[levelIndex, stepIndex]`, or null. */
export function locateStep(d, stepKey) {
  for (let li = 0; li < d.levels.length; li += 1) {
    const si = d.levels[li].steps.findIndex((s) => s.key === stepKey);
    if (si >= 0) return [li, si];
  }
  return null;
}

/**
 * Whether a step can move by `delta` (-1 up, +1 down) in the FLATTENED structure. A step
 * at the edge of its level moves into the neighbouring level, so the only immovable
 * positions are the very first and the very last step overall.
 */
export function canMoveStep(d, stepKey, delta) {
  const at = locateStep(d, stepKey);
  if (!at) return false;
  const [li, si] = at;
  if (delta < 0) return si > 0 || li > 0;
  return si < d.levels[li].steps.length - 1 || li < d.levels.length - 1;
}

/**
 * The draft with one step moved by `delta` (see `canMoveStep`): within its level, or
 * across the boundary into the level above (as its last step) or below (as its first).
 * This is the keyboard path between levels; dragging stays within a level.
 */
export function moveStep(d, stepKey, delta) {
  if (!canMoveStep(d, stepKey, delta)) return d;
  const [li, si] = locateStep(d, stepKey);
  const levels = d.levels.map((l) => ({ ...l, steps: [...l.steps] }));
  const [step] = levels[li].steps.splice(si, 1);
  const within = delta < 0 ? si > 0 : si < d.levels[li].steps.length - 1;
  if (within) levels[li].steps.splice(si + delta, 0, step);
  else if (delta < 0) levels[li - 1].steps.push(step);
  else levels[li + 1].steps.unshift(step);
  return { ...d, levels };
}

/** The draft with one level moved by `delta`. Unchanged at either end. */
export function moveLevel(d, levelKey, delta) {
  const i = d.levels.findIndex((l) => l.key === levelKey);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= d.levels.length) return d;
  const levels = [...d.levels];
  [levels[i], levels[j]] = [levels[j], levels[i]];
  return { ...d, levels };
}

const isPermutation = (keys, byKey) => Array.isArray(keys)
  && keys.length === byKey.size
  && new Set(keys).size === keys.length
  && keys.every((k) => byKey.has(k));

/** The draft with its levels in the order of `keys` (a drag result); unchanged unless a permutation. */
export function reorderLevels(d, keys) {
  const byKey = new Map(d.levels.map((l) => [l.key, l]));
  if (!isPermutation(keys, byKey)) return d;
  return { ...d, levels: keys.map((k) => byKey.get(k)) };
}

/** The draft with one level's steps in the order of `keys`; unchanged unless a permutation. */
export function reorderSteps(d, levelKey, keys) {
  const level = d.levels.find((l) => l.key === levelKey);
  if (!level) return d;
  const byKey = new Map(level.steps.map((s) => [s.key, s]));
  if (!isPermutation(keys, byKey)) return d;
  return {
    ...d,
    levels: d.levels.map((l) => (l.key === levelKey ? { ...l, steps: keys.map((k) => byKey.get(k)) } : l)),
  };
}

/**
 * The draft with `step` written into level `levelKey`: replacing the step of the same key
 * where it already is, or appended to that level when it is new.
 */
export function upsertStep(d, levelKey, step) {
  if (locateStep(d, step.key)) return mapStep(d, step.key, () => step);
  return {
    ...d,
    levels: d.levels.map((l) => (l.key === levelKey ? { ...l, steps: [...l.steps, step] } : l)),
  };
}

/** The draft with one step replaced by `fn(step)`. */
export function mapStep(d, stepKey, fn) {
  return {
    ...d,
    levels: d.levels.map((l) => (l.steps.some((s) => s.key === stepKey)
      ? { ...l, steps: l.steps.map((s) => (s.key === stepKey ? fn(s) : s)) }
      : l)),
  };
}

/** The draft without one step. */
export const removeStep = (d, stepKey) => ({
  ...d,
  levels: d.levels.map((l) => ({ ...l, steps: l.steps.filter((s) => s.key !== stepKey) })),
});

/** The draft without one level; the last level of a pathway is never removed. */
export const removeLevel = (d, levelKey) => (d.levels.length <= 1
  ? d
  : { ...d, levels: d.levels.filter((l) => l.key !== levelKey) });

// ── activation problems, located ─────────────────────────────────────────────

/**
 * Where an activation problem (contract §4) points in the DRAFT: `{tab, levelKey?,
 * stepKey?}`. `OBJECTIVE_REQUIRED` and `COHORT_DATES_REQUIRED` are fixed on the Basics
 * tab; everything else on the Structure tab, at the level or step the problem names when
 * the draft still holds it (a saved row's key is its id).
 *
 * @param {object} d Draft
 * @param {{ code: string, level_id?: string, step_id?: string }} problem
 * @returns {{ tab: "basics"|"structure", levelKey: string|null, stepKey: string|null }}
 */
export function locateProblem(d, problem) {
  if (problem.code === "OBJECTIVE_REQUIRED" || problem.code === "COHORT_DATES_REQUIRED") {
    return { tab: "basics", levelKey: null, stepKey: null };
  }
  const level = problem.level_id ? d.levels.find((l) => l.id === problem.level_id) : null;
  const at = problem.step_id
    ? d.levels.flatMap((l) => l.steps).find((s) => s.id === problem.step_id)
    : null;
  return { tab: "structure", levelKey: level ? level.key : null, stepKey: at ? at.key : null };
}

/**
 * The titles a problem sentence needs: `level` ("Level 2 · Practice") and `step`. Empty
 * strings when the draft no longer holds the row the problem names.
 *
 * @param {object} d Draft
 * @param {{ level_id?: string, step_id?: string }} problem
 * @param {(n: number, title: string) => string} levelLabel  the screen's "Level {n} · {title}"
 */
export function problemSubjects(d, problem, levelLabel) {
  const li = problem.level_id ? d.levels.findIndex((l) => l.id === problem.level_id) : -1;
  const step = problem.step_id ? d.levels.flatMap((l) => l.steps).find((s) => s.id === problem.step_id) : null;
  return {
    level: li >= 0 ? levelLabel(li + 1, trimmed(d.levels[li].title)) : "",
    step: step ? trimmed(step.title) : "",
  };
}
